// src/components/checklists/ChecklistRemovedMedia.js
// chklsthide-4f8b2d61 — "kept on record", made visible (web). Office roles see the photos and
// signatures removed from one checklist question: greyed, with the item's full history newest
// first ("Removed by {name} · {when}", "Restored by …"; mediaEvents). Clicking one opens the
// full-size original. An Owner / Admin / Manager can Restore one (restoreChecklistMedia); the
// server re-checks the role, the lock and the 10-photo cap. Removed items never count, and are
// never customer-facing (checklist notes are internal; their media has no portal path).

import React, { useEffect, useRef, useState } from 'react';
import { getDownloadURL, ref } from 'firebase/storage';
import { Loader2, RotateCcw } from 'lucide-react';
import { storage } from '../../firebase/config';
import { buildStoragePath } from '../../utils/notesMediaUpload';
import { restoreChecklistMedia, mediaEvents } from '../../utils/checklistClient';

const whenText = (ts) => {
    const d = ts?.toDate ? ts.toDate() : (ts instanceof Date ? ts : null);
    return d ? d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
};
const byLine = (verb, who, ts) => `${verb} by ${who?.name || 'someone'}${whenText(ts) ? ` · ${whenText(ts)}` : ''}`;

// A download URL, or null if it can't be had (missing file, no access, or no answer in time).
// Never hangs: "Loading…" always ends in a picture or "Couldn't load".
const LOAD_TIMEOUT_MS = 20000;
const urlOrNull = (path) => {
    if (!path) return Promise.resolve(null);
    let timer;
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(null), LOAD_TIMEOUT_MS); });
    return Promise.race([getDownloadURL(ref(storage, path)).catch(() => null), timeout]).finally(() => clearTimeout(timer));
};

const ChecklistRemovedMedia = ({
    orgId, noteId, jobId, checklistId, questionId, isSignature,
    removed,      // hidden notesMedia docs for this question, newest removal first
    canRestore,   // Owner / Admin / Manager
    editable,     // the checklist is not locked
}) => {
    const [open, setOpen] = useState(false);
    // { mediaId: { status: 'loading' | 'ready' | 'failed', src, href } } — src is the thumbnail when
    // the server made one (else the original); href is the full-size original (else the thumbnail).
    const [imgs, setImgs] = useState({});
    const [restoringIds, setRestoringIds] = useState(() => new Set());
    const [message, setMessage] = useState('');
    // chklsthide-4f8b2d61 fix: set true IN the effect. React StrictMode (development) mounts,
    // unmounts and re-mounts; a ref only cleared on unmount stayed false for good, so every
    // resolved URL was dropped and the tile sat on "Loading…" forever.
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const load = (m) => {
        setImgs(prev => ({ ...prev, [m.id]: { status: 'loading' } }));
        Promise.all([urlOrNull(m.thumbnailPath), urlOrNull(buildStoragePath(orgId, noteId, m.id))]).then(([thumb, full]) => {
            if (!mountedRef.current) return;
            const src = thumb || full;
            setImgs(prev => ({ ...prev, [m.id]: src ? { status: 'ready', src, href: full || thumb } : { status: 'failed' } }));
        });
    };
    const markFailed = (id) => setImgs(prev => ({ ...prev, [id]: { status: 'failed' } }));

    // Every removed item resolves once the list is opened (and any item removed while it's open).
    useEffect(() => {
        if (!open) return;
        removed.forEach((m) => { if (!imgs[m.id]) load(m); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, removed, orgId, noteId]);

    // "Restoring…" ends when the item leaves the removed list (the listener sees hidden: false).
    useEffect(() => {
        const ids = new Set(removed.map(m => m.id));
        setRestoringIds(prev => (prev.size ? new Set([...prev].filter(id => ids.has(id))) : prev));
    }, [removed]);

    if (!removed.length) return null;

    const handleRestore = async (m) => {
        const what = isSignature ? 'signature' : 'photo';
        if (!window.confirm(`Restore this ${what}? It goes back on the checklist and counts again.`)) return;
        setMessage('');
        setRestoringIds(prev => new Set(prev).add(m.id));
        try {
            await restoreChecklistMedia({ jobId, checklistId, questionId, mediaId: m.id });
        } catch (err) {
            if (!mountedRef.current) return;
            setRestoringIds(prev => { const next = new Set(prev); next.delete(m.id); return next; });
            setMessage(err?.message || `That ${what} could not be restored. Try again.`);
        }
    };

    return (
        <div className="space-y-1.5">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="text-[11px] text-slate-400 hover:text-slate-600 hover:underline"
            >
                {removed.length} removed — kept on record · {open ? 'Hide removed' : 'Show removed'}
            </button>
            {open && (
                <div className="space-y-2">
                    {removed.map((m) => {
                        const img = imgs[m.id] || { status: 'loading' };
                        const busy = restoringIds.has(m.id);
                        return (
                            <div key={m.id} className="flex items-start gap-2">
                                <div className={`${isSignature ? 'w-40 h-16' : 'w-16 h-16'} shrink-0 rounded-lg overflow-hidden border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center`}>
                                    {img.status === 'ready' && (
                                        <a href={img.href} target="_blank" rel="noopener noreferrer" className="block w-full h-full opacity-60 grayscale" title="Open full size">
                                            <img src={img.src} alt={isSignature ? 'Removed signature' : 'Removed photo'} onError={() => markFailed(m.id)}
                                                className={`w-full h-full ${isSignature ? 'object-contain bg-white' : 'object-cover'}`} />
                                        </a>
                                    )}
                                    {img.status === 'loading' && <span className="text-[10px] text-slate-400">Loading…</span>}
                                    {img.status === 'failed' && (
                                        <button type="button" onClick={() => load(m)} title="Try loading it again"
                                            className="w-full h-full flex flex-col items-center justify-center text-[10px] font-semibold text-red-600 hover:bg-red-50">
                                            Couldn't load
                                            <span className="font-normal text-slate-500 underline">Retry</span>
                                        </button>
                                    )}
                                </div>
                                <div className="text-[11px] text-slate-500 space-y-0.5 pt-0.5">
                                    {mediaEvents(m).map((e, i) => (
                                        <p key={i} className={i === 0 ? '' : 'text-slate-400'}>
                                            {byLine(e.action === 'hidden' ? 'Removed' : 'Restored', e.by, e.at)}
                                            {e.reason ? ` — "${e.reason}"` : ''}
                                        </p>
                                    ))}
                                    {canRestore && editable && (busy
                                        ? <span className="inline-flex items-center gap-1 font-semibold text-slate-500"><Loader2 size={11} className="animate-spin" /> Restoring…</span>
                                        : <button type="button" onClick={() => handleRestore(m)}
                                            className="inline-flex items-center gap-1 font-semibold text-[#5F4EC9] hover:underline">
                                            <RotateCcw size={11} /> Restore
                                          </button>)}
                                </div>
                            </div>
                        );
                    })}
                    {message && <p className="text-xs text-red-600">{message}</p>}
                </div>
            )}
        </div>
    );
};

export default ChecklistRemovedMedia;
